import { useEffect, useState } from "react";
import { MessageSquare, ArrowRight, RefreshCcw, Inbox } from "lucide-react";
import { api } from "@/lib/api";

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function CustomerMessagesInbox({ nav }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/messages/inbox");
      setThreads(r.data?.threads || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="card-default p-5" data-testid="advisor-messages-inbox">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 text-primary" />
          <h2 className="font-display font-bold text-base">Customer messages</h2>
        </div>
        <div className="flex items-center gap-2">
          {threads.length > 0 && (
            <span className="text-[10px] uppercase tracking-wider text-destructive font-mono-plex">
              {threads.reduce((s, t) => s + (t.unread_count || 0), 0)} unread
            </span>
          )}
          <button onClick={load} className="text-muted-foreground hover:text-foreground p-1" title="Refresh" data-testid="messages-inbox-refresh">
            <RefreshCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Pre-call replies and customer messages from quote pages.
      </p>

      {loading ? (
        <div className="text-xs text-muted-foreground py-4">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="text-xs text-muted-foreground italic py-4 text-center" data-testid="messages-inbox-empty">
          No unread messages right now. Customers can reach you any time from their quote page or by replying to your pre-call.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="messages-inbox-list">
          {threads.map((t) => (
            <li
              key={t.job_id}
              onClick={() => nav(`/jobs/${t.job_id}`)}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer"
              data-testid={`messages-inbox-row-${t.job_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-sm truncate">{t.customer_name || "—"}</span>
                  <span className="text-xs text-muted-foreground font-mono-plex">{t.vehicle_reg || ""}</span>
                  {t.unread_count > 1 && (
                    <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground font-mono-plex">
                      {t.unread_count} new
                    </span>
                  )}
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground font-mono-plex shrink-0">
                    {relativeTime(t.last_at)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  <MessageSquare className="w-3 h-3 inline mr-1 -mt-0.5" />
                  {t.last_body || ""}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
